/**
 * IFRAP-AI.fr - Admin Dashboard
 * Vue d'ensemble du back-office - Version Chaleureuse
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BootScreen from '../../components/system/BootScreen';
import TutorialSystem from '../../components/system/TutorialSystem';
import { 
  HelpCircle, X, Sparkles, Terminal, FileText, BarChart3, 
  Search, Mic, Mail, Euro, Settings, ArrowRight, Activity,
  Database, Server, CheckCircle, AlertCircle, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Horloge temps réel
const RealTimeClock = () => {
  const [time, setTime] = React.useState(new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-100" data-testid="realtime-clock">
      <Clock className="w-4 h-4 text-[#000091]" />
      <span className="text-sm font-mono text-gray-800 tabular-nums">
        {time.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
        {' '}
        <span className="font-bold text-[#000091]">
          {time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      </span>
    </div>
  );
};

// Popup de proposition de tutoriel - Version chaleureuse
const TutorialPrompt = ({ onAccept, onDecline, userName }) => {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl max-w-md w-full p-6 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-[#000091] to-[#1a1a6e] rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Bonjour {userName} !
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              De nouvelles fonctionnalités vous attendent. Voulez-vous les découvrir ?
            </p>
            <ul className="text-sm text-gray-500 space-y-2 mb-5">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Compteurs temps réel avec mode TV plein écran</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Dictée vocale pour les chroniques (Speech-to-Text)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Scraper IFRAP avancé (URL ou mot-clé)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Sources et dates sur chaque indicateur</span>
              </li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={onAccept}
                className="flex-1 bg-[#000091] text-white font-medium py-2.5 px-4 rounded-lg hover:bg-[#000091]/90 transition-all shadow-md hover:shadow-lg text-sm"
                data-testid="tutorial-accept-btn"
              >
                Oui, montrez-moi
              </button>
              <button
                onClick={onDecline}
                className="flex-1 bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                data-testid="tutorial-decline-btn"
              >
                Plus tard
              </button>
            </div>
          </div>
          <button
            onClick={onDecline}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBoot, setShowBoot] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showTutorialPrompt, setShowTutorialPrompt] = useState(false);

  // Prénom pour le message de bienvenue
  const getFirstName = () => {
    if (user?.full_name) {
      return user.full_name.split(' ')[0];
    }
    if (user?.email === 'averdier@ifrap.org') return 'Agnès';
    if (user?.email === 'jbgiraud@mac.com') return 'Jean-Baptiste';
    return user?.email?.split('@')[0] || 'Opérateur';
  };

  useEffect(() => {
    // Check if boot screen should be shown
    const bootShown = sessionStorage.getItem('ifrap_boot_shown');
    if (!bootShown) {
      setShowBoot(true);
    } else {
      // Toujours proposer le tutoriel à chaque connexion
      const promptShownThisSession = sessionStorage.getItem('ifrap_tutorial_prompt_shown');
      if (!promptShownThisSession) {
        setShowTutorialPrompt(true);
        sessionStorage.setItem('ifrap_tutorial_prompt_shown', 'true');
      }
    }
  }, []);

  const handleBootComplete = () => {
    sessionStorage.setItem('ifrap_boot_shown', 'true');
    setShowBoot(false);
    // Proposer le tutoriel après le boot
    setShowTutorialPrompt(true);
    sessionStorage.setItem('ifrap_tutorial_prompt_shown', 'true');
  };

  const handleTutorialAccept = () => {
    setShowTutorialPrompt(false);
    setShowTutorial(true);
  };

  const handleTutorialDecline = () => {
    setShowTutorialPrompt(false);
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API}/status`);
        const data = await response.json();
        setStatus(data);
      } catch (err) {
        console.error('Failed to fetch status:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    // Poll every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Determine welcome message based on user role
  const isPresidente = user?.roles?.includes('presidente') || user?.email === 'averdier@ifrap.org';
  const isSuperAdmin = user?.roles?.includes('super_admin');
  const welcomeName = getFirstName();

  // Modules principaux - Actifs
  const activeModules = [
    {
      name: 'Console de supervision',
      description: 'Suivez l\'activité du système en temps réel',
      path: '/admin/console',
      icon: Terminal,
      color: 'from-slate-600 to-slate-700',
      badge: null,
    },
    {
      name: 'Préparation Chronique',
      description: 'Préparez vos interventions média avec l\'IA',
      path: '/admin/chronique',
      icon: Mic,
      color: 'from-[#E1000F] to-[#B8000C]',
      badge: 'Nouveau',
    },
    {
      name: 'Demandes de données',
      description: 'Gérez vos demandes aux administrations',
      path: '/admin/data-request',
      icon: FileText,
      color: 'from-amber-500 to-amber-600',
      badge: 'Nouveau',
    },
    {
      name: 'Suivi des coûts IA',
      description: 'Visualisez l\'utilisation de l\'IA',
      path: '/admin/costs',
      icon: Euro,
      color: 'from-emerald-500 to-emerald-600',
      badge: null,
    },
    {
      name: 'Listes de diffusion',
      description: 'Gérez vos contacts et mailings',
      path: '/admin/distribution',
      icon: Mail,
      color: 'from-blue-500 to-blue-600',
      badge: null,
    },
    {
      name: 'Configuration',
      description: 'Paramètres SMTP et système',
      path: '/admin/config',
      icon: Settings,
      color: 'from-gray-500 to-gray-600',
      badge: null,
    },
    {
      name: 'Scraper IFRAP',
      description: 'Scrapez ifrap.org par URL ou mot-clé',
      path: '/admin/scraper',
      icon: Search,
      color: 'from-purple-500 to-purple-600',
      badge: 'Nouveau',
    },
  ];

  // Modules en développement
  const upcomingModules = [
    {
      name: 'Base de Connaissances',
      description: 'Livres, infographies, profils de style IA',
      path: '/admin/documents',
      icon: FileText,
    },
    {
      name: 'Dashboard KPIs',
      description: '15 indicateurs économiques en temps réel',
      path: '/admin/kpis',
      icon: BarChart3,
    },
    {
      name: 'Module OSINT',
      description: 'Surveillance automatisée des sources',
      path: '/admin/osint',
      icon: Search,
    },
  ];

  return (
    <>
      {/* Boot Screen */}
      {showBoot && <BootScreen onComplete={handleBootComplete} />}
      
      {/* Tutorial Prompt */}
      {showTutorialPrompt && (
        <TutorialPrompt 
          onAccept={handleTutorialAccept}
          onDecline={handleTutorialDecline}
          userName={welcomeName}
        />
      )}
      
      {/* Tutorial System */}
      {showTutorial && <TutorialSystem onComplete={handleTutorialComplete} />}
      
      <div className="space-y-8 pb-12" data-testid="admin-dashboard">
        {/* En-tête de bienvenue */}
        <div className="bg-gradient-to-r from-[#000091]/5 via-[#000091]/10 to-transparent rounded-2xl p-8 border border-[#000091]/10">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Bonjour {welcomeName} !
              </h1>
              <p className="text-gray-600 mt-2 text-lg">
                {isPresidente 
                  ? 'Bienvenue sur votre espace de travail IFRAP-AI' 
                  : isSuperAdmin 
                    ? 'Votre tableau de bord est prêt'
                    : 'Que souhaitez-vous faire aujourd\'hui ?'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border border-gray-100">
                <Activity className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-gray-700">Opérationnel</span>
              </div>
              <RealTimeClock />
            </div>
          </div>
        </div>

        {/* Statut système - Version épurée */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm font-medium">API Backend</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs text-emerald-600 font-medium">Actif</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-gray-400" />
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : status?.status || 'OK'}
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm font-medium">Base de données</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                <span className="text-xs text-amber-600 font-medium">Mémoire</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-400" />
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : (status?.config?.supabase_configured ? 'PostgreSQL' : 'En mémoire')}
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm font-medium">Logs système</span>
            </div>
            <div className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-gray-400" />
              <p className="text-2xl font-semibold text-gray-900">
                {loading ? '...' : status?.logs_count || 0}
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-sm font-medium">Environnement</span>
            </div>
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              <p className="text-2xl font-semibold text-[#000091]">
                {loading ? '...' : status?.config?.environment || 'Preview'}
              </p>
            </div>
          </div>
        </div>

        {/* Modules actifs */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Vos outils
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeModules.map((module) => {
              const IconComponent = module.icon;
              return (
                <Link
                  key={module.path}
                  to={module.path}
                  className="group bg-white border border-gray-100 rounded-xl p-6 transition-all hover:shadow-lg hover:border-[#000091]/20 hover:-translate-y-0.5"
                  data-testid={`module-${module.path.replace('/admin/', '')}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${module.color} flex items-center justify-center shadow-md`}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    {module.badge && (
                      <span className="text-xs font-medium text-white bg-[#E1000F] px-2 py-1 rounded-full">
                        {module.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-[#000091] transition-colors">
                    {module.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {module.description}
                  </p>
                  <div className="flex items-center text-sm font-medium text-[#000091] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Accéder</span>
                    <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Modules à venir */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Prochainement
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            Ces modules sont en cours de développement et seront bientôt disponibles.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {upcomingModules.map((module) => {
              const IconComponent = module.icon;
              return (
                <div
                  key={module.path}
                  className="bg-gray-50 border border-gray-200 border-dashed rounded-xl p-5 opacity-60"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-600">{module.name}</h3>
                      <p className="text-xs text-gray-400">{module.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Aide rapide */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-6 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-[#000091]/10 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-5 h-5 text-[#000091]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Besoin d'aide ?</h3>
              <p className="text-sm text-gray-600">
                L'équipe technique est à votre disposition pour vous accompagner dans l'utilisation de la plateforme.
                N'hésitez pas à nous contacter pour toute question.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
